import React, { useEffect, useMemo, useRef, useState } from "react";
import { icons } from "lucide-react";
import { format, parseISO } from "date-fns";

export const C = {
  orange: "#ee5827",
  primary: "#27306c",
  secondary: "rgba(232, 234, 241, 1)",
  secondaryDark: "rgba(210, 214, 228, 1)",
  white: "#ffffff",
  textDark: "#1f2937",
  textMid: "#6b7280",
  green: "#16a34a",
  red: "#dc2626",
  amber: "#d97706",
};

export const Icon = ({ name, ...props }) => {
  const Component = icons[name];
  return Component ? <Component {...props} /> : null;
};

export const MODULES = [
  { value: "academic", label: "Academic" },
  { value: "administrative", label: "Administrative" },
  { value: "commercial", label: "Commercial" },
  { value: "financial", label: "Financial" },
  { value: "dso", label: "DSO" },
  { value: "reports", label: "Reports" },
  { value: "settings", label: "Settings" },
  { value: "other", label: "Other" },
];

export const STATUS = {
  pending_approval: { label: "Pending", color: C.amber },
  changes_requested: { label: "Changes", color: C.amber },
  approved: { label: "Approved", color: C.green },
  in_progress: { label: "In progress", color: C.primary },
  rejected: { label: "Rejected", color: C.red },
  closed: { label: "Closed", color: C.textMid },
  archived: { label: "Archived", color: C.textMid },
};

export const EVENT_ICON = {
  created: ["CirclePlus", C.primary],
  approved: ["ThumbsUp", C.green],
  rejected: ["ThumbsDown", C.red],
  changes_requested: ["Pencil", C.amber],
  start: ["Play", C.primary],
  close: ["CircleCheck", C.green],
  archive: ["Archive", C.textMid],
  version_created: ["CopyPlus", C.primary],
  comment: ["MessageSquare", C.textMid],
  assign: ["UserCheck", C.primary],
  escalate: ["ArrowUp", C.amber],
  return: ["Undo2", C.primary],
};

export const moduleFromPath = (pathname = window.location.pathname) => {
  const first = pathname.split("/")[1]?.toLowerCase();
  return MODULES.some((m) => m.value === first) ? first : "other";
};

export const formatDate = (value) =>
  value ? format(parseISO(value), "MMM d, yyyy h:mm a") : "";

export const errorMessage = (error) =>
  error?.response?.data?.error || "Something went wrong. Please try again.";

export function StatusChip({ status }) {
  const { label, color } = STATUS[status] || { label: status, color: C.textMid };
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 99, padding: "1px 8px", whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

export function IconButton({ name, title, onClick, color = C.primary, disabled, size = 16, solid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8,
        border: `1px solid ${solid ? color : C.secondaryDark}`, background: solid ? color : C.white,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, flexShrink: 0,
      }}
    >
      <Icon name={name} size={size} color={solid ? C.white : color} />
    </button>
  );
}

export const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 12, borderRadius: 8,
  border: `1px solid ${C.secondaryDark}`, background: C.white, color: C.textDark, outline: "none", fontFamily: "inherit",
};

export function Thumb({ file, onRemove }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const kind = file.type.startsWith("video/") ? "video" : file.type.startsWith("image/") ? "image" : "pdf";
  return (
    <div style={{ position: "relative", width: 52, height: 52, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.secondaryDark}`, background: C.secondary, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {kind === "image" ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Icon name={kind === "video" ? "Video" : "FileText"} size={20} color={C.primary} />}
      <button type="button" onClick={onRemove} style={{ position: "absolute", top: 1, right: 1, width: 16, height: 16, borderRadius: 99, border: "none", background: "rgba(0,0,0,0.6)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
        <Icon name="X" size={10} color="#fff" />
      </button>
    </div>
  );
}

export function Attachments({ files, setFiles, max = 10 }) {
  const input = useRef(null);
  const add = (list) => setFiles((current) => [...current, ...Array.from(list)].slice(0, max));
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      <IconButton name="Paperclip" title="Attach images, videos or PDFs" onClick={() => input.current?.click()} />
      <input ref={input} type="file" multiple accept="image/*,video/*,application/pdf" hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {files.map((file, i) => (
        <Thumb key={`${file.name}-${i}`} file={file} onRemove={() => setFiles((current) => current.filter((_, j) => j !== i))} />
      ))}
    </div>
  );
}

const isAllowed = (file) => /^(image|video)\//.test(file.type) || file.type === "application/pdf";

export const formatMinutes = (minutes) => {
  if (!minutes) return "-";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours}h ${rest}m` : `${rest}m`;
};

export const nowLocal = () => format(new Date(), "yyyy-MM-dd'T'HH:mm");

export const elapsedMinutes = (openedAt, until) => Math.max(0, Math.floor((new Date(until) - parseISO(openedAt)) / 60000));

export const splitMinutes = (total) => ({
  hours: total >= 60 ? String(Math.floor(total / 60)) : "",
  minutes: String(total % 60),
});

export function ResolvedAtField({ value, onChange, onNow, openedAt }) {
  const ago = elapsedMinutes(openedAt, new Date());
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.textDark }}>Resolved at</span>
        <input
          type="datetime-local"
          value={value}
          min={format(parseISO(openedAt), "yyyy-MM-dd'T'HH:mm")}
          max={nowLocal()}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, width: "auto" }}
        />
        <button type="button" onClick={() => (onNow ? onNow() : onChange(nowLocal()))} style={{ fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.secondaryDark}`, background: C.white, color: C.primary, cursor: "pointer" }}>
          Now
        </button>
        <span style={{ marginLeft: "auto", fontSize: 12, color: C.textMid }}>
          Ticket opened <strong style={{ color: C.textDark }}>{ago < 1 ? "just now" : `${formatMinutes(ago)} ago`}</strong>
        </span>
      </div>
    </div>
  );
}

export const toMinutes = ({ hours, minutes }) => (Number(hours) || 0) * 60 + (Number(minutes) || 0);

export function WorkedField({ value, onChange }) {
  const field = { ...inputStyle, width: 76, textAlign: "center" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12, color: C.textMid }}>
        <span style={{ fontWeight: 600, color: C.textDark, marginRight: 4 }}>Time spent on the solution</span>
        <input type="number" min={0} placeholder="0" value={value.hours} onChange={(e) => onChange({ ...value, hours: e.target.value })} style={field} /> h
        <input type="number" min={0} max={59} placeholder="0" value={value.minutes} onChange={(e) => onChange({ ...value, minutes: e.target.value })} style={field} /> min
      </div>
    </div>
  );
}

export const pastedFiles = (event) => {
  const data = event.clipboardData || event.dataTransfer;
  const direct = Array.from(data?.files || []);
  const list = direct.length
    ? direct
    : Array.from(data?.items || []).filter((item) => item.kind === "file").map((item) => item.getAsFile()).filter(Boolean);
  return list.filter(isAllowed);
};

export function useFileDrop(setFiles, max = 10) {
  const [over, setOver] = useState(false);
  const add = (list) => list.length && setFiles((current) => [...current, ...list].slice(0, max));
  return {
    over,
    handlers: {
      onPaste: (e) => {
        const found = pastedFiles(e);
        if (!found.length) return;
        e.preventDefault();
        add(found);
      },
      onDragOver: (e) => { e.preventDefault(); setOver(true); },
      onDragLeave: () => setOver(false),
      onDrop: (e) => { e.preventDefault(); setOver(false); add(pastedFiles(e)); },
    },
  };
}

export function buildFormData(fields, files) {
  const data = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") data.append(key, value);
  });
  files.forEach((file) => data.append("files", file));
  return data;
}

export function AttachmentGrid({ items = [] }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {items.map((item) =>
        item.kind === "video" ? (
          <video key={item.id} src={item.url} controls style={{ width: 160, borderRadius: 8, background: "#000" }} />
        ) : item.kind === "image" ? (
          <a key={item.id} href={item.url} target="_blank" rel="noreferrer">
            <img src={item.url} alt={item.filename} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.secondaryDark}` }} />
          </a>
        ) : (
          <a key={item.id} href={item.url} target="_blank" rel="noreferrer" title={item.filename} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: 8, border: `1px solid ${C.secondaryDark}`, background: C.white }}>
            <Icon name="FileText" size={24} color={C.primary} />
          </a>
        )
      )}
    </div>
  );
}
